// The Make plan (ERP-PLAN §5.4 "Make dashboard"): items contract manufacturers build (a BOM whose make vendor is
// the CM), per CM. Builds: weeks of cover, the next build (quantity, start-by, finished at HHL), component
// readiness (what's at the CM, on its way, short, and the gating part with its ETA), what the CM can build today and
// open make POs. Parts to send: what HHL must buy or ship to each CM, by when, with the plan's order for it.
// Timeline: per project, parts landing, builds and finished goods at HHL. Picked builds and parts go onto draft POs
// in one click (one per vendor, ship-to and kind; builds as make POs). Everything is worked out in the browser from
// synced data (shared/planning.ts with BOMs, shared/make.ts via lib/make.ts), so nothing waits.
// ?tab=builds|consignment|timeline, ?cm=<vendor id>, ?project=<id>, ?item= opens an item's detail.
// Builds and Parts to send are the usual table (DataTable: a band per CM, ticks for what goes on draft POs, views,
// columns, Copy / CSV): j/k move, Space ticks, Enter (or a click) opens the detail, and with it open j/k step through.
import { AlertTriangle, CheckCircle2, ExternalLink, Factory, FilePlus2 } from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { buildText, type CmView, type ConsignLine, type DraftPick, draftGroups, type MakeRow, type Readiness, runForBuilds } from "../../../../shared/make.ts";
import { addPlanDays, planDayText as dayText, fmt, type ItemPlanResult, type MrpOrder, ownLead, type Plan, shortDate } from "../../../../shared/planning.ts";
import { mulRound, priceFor, validQty } from "../../../../shared/po.ts";
import { money } from "../../lib/format.ts";
import { useMakeDashboard } from "../../lib/make.ts";
import { can } from "../../lib/mutate.ts";
import { createDraftPo } from "../../lib/planning.ts";
import { Link, setSearch, useSearch } from "../../lib/router.tsx";
import { useRow, useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, EmptyState, Select, Thumb } from "../../ui/controls.tsx";
import { CellControls, type Column, DataTable, type TableSection } from "../../ui/DataTable.tsx";
import { PageSearch, useDetailKeys } from "../../ui/ListPage.tsx";
import { PaneLink } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { ClosureChip, ClosuresButton } from "../Closures.tsx";
import { CutoverBanner } from "../Cutover.tsx";
import { ShipFromHhl } from "../consignment/Shipments.tsx"; // "Ship N from HHL" opens the shipment, filled in
import { ForecastSection } from "../ForecastSection.tsx";
import { BUCKET_TONE, ItemPlanSection } from "../ItemPlanSection.tsx";
import { PlanDetail, WhyButton } from "../PlanDetail.tsx";
import { poPath } from "../po/lib.ts";
import { StockStaleBanner } from "../SyncHealth.tsx";
import { AutoRunButton } from "./AutoRun.tsx";
import { MakeTimeline } from "./MakeTimeline.tsx";

type Tab = "builds" | "consignment" | "timeline";
const TABS: { tab: Tab; label: string; hint: string }[] = [
  { tab: "builds", label: "Builds", hint: "Each make item's next build and whether its parts are ready" },
  { tab: "consignment", label: "Parts to send", hint: "What HHL must buy or ship to each CM, and by when, for the next builds" },
  { tab: "timeline", label: "Timeline", hint: "Per project: parts landing at the CM, builds, and finished goods at HHL" },
];

const vendorName = (v: { name: string; short_name: string } | null | undefined) => (v ? v.short_name || v.name : "Unknown CM");

/** "Nov 20", "Today", "Now (12 days late)". */
function byText(day: number, today: string) {
  if (day < 0) return `Now (${fmt(-day)} day${day === -1 ? "" : "s"} late)`;
  if (day === 0) return "Today";
  return shortDate(addPlanDays(today, day), today);
}

/** What a consignment line would order: the plan's order for it to this CM, else the shortfall rounded to the vendor's terms. */
function consignOrder(c: ConsignLine, cm: CmView): { qty: number; shipTo: string | null } | null {
  if (!c.result || c.buy <= 0) return null;
  if (c.order) return { qty: c.order.qty, shipTo: cm.site ?? c.order.shipTo };
  return { qty: c.result.terms ? validQty(c.result.terms, Math.ceil(c.buy)) : Math.ceil(c.buy), shipTo: cm.site };
}

const buildKey = (r: MakeRow) => `b:${r.item.id}`;
/** One line per item, ship-to and kind. */
const pickId = (p: DraftPick) => `${p.result.item.id}|${p.shipTo}|${p.kind}`;
const consignKey = (cm: CmView, c: ConsignLine) => `c:${cm.vendorId}:${c.itemId}`;
/** Picked unless unticked: builds and parts to order within the review period (or late). */
const buildDue = (r: MakeRow, review: number) => !!r.next && r.result.planned && r.result.bucket !== "snoozed" && r.next.orderBy <= review;
const consignDue = (c: ConsignLine, review: number) => c.buy > 0 && (c.orderBy ?? 0) <= review;

export function MakePlanPage() {
  const { plan, dash } = useMakeDashboard();
  const params = useSearch();
  const tabParam = params.get("tab") as Tab | null;
  const tab: Tab = tabParam && TABS.some((t) => t.tab === tabParam) ? tabParam : "builds";
  const cmFilter = params.get("cm") ?? "";
  const projectFilter = params.get("project") ?? "";
  const openId = params.get("item");
  const projects = useRows("project");
  const [query, setQuery] = useState("");
  // Due ones are picked unless unticked; others when ticked.
  const [toggled, setToggled] = useState<Set<string>>(() => new Set());
  const [created, setCreated] = useState<{ id: string; number: string; label: string }[]>([]);
  const [busy, setBusy] = useState(false);
  // Orders just put on a draft PO stay out of the picks until the plan shows that PO (so a second click can't repeat them).
  const [awaiting, setAwaiting] = useState<Map<string, string>>(() => new Map());
  const searchRef = useRef<HTMLInputElement>(null);
  // Table views --- the Builds and Parts to send tables' View and Columns menus, by the title
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const canOrder = can("po.create");
  const review = plan.settings.review_days;

  const cms = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    const matches = (r: MakeRow) =>
      (!projectFilter || r.projectIds.includes(projectFilter)) &&
      (!terms.length || terms.every((t) => `${r.item.sku} ${r.item.name} ${vendorName(r.vendor)}`.toLowerCase().includes(t)));
    return dash.cms
      .filter((c) => !cmFilter || c.vendorId === cmFilter)
      .map((c) => ({ ...c, rows: c.rows.filter(matches) }))
      .filter((c) => c.rows.length);
  }, [dash, cmFilter, projectFilter, query]);

  // Picks: builds (make POs to the CM) and consignment parts (POs shipping to the CM).
  const picked = (key: string, due: boolean) => due !== toggled.has(key);
  const picks = useMemo(() => {
    const isPicked = (key: string, due: boolean) => due !== toggled.has(key);
    const out: (DraftPick & { label: string })[] = [];
    for (const cm of cms) {
      for (const r of cm.rows) {
        if (r.next && isPicked(buildKey(r), buildDue(r, review)))
          out.push({ result: r.result, qty: r.next.qty, shipTo: r.next.shipTo, kind: "make", label: `${r.item.sku} build` });
      }
      for (const c of cm.consignment) {
        const o = consignOrder(c, cm);
        if (o && c.result && isPicked(consignKey(cm, c), consignDue(c, review)))
          out.push({ result: c.result, qty: o.qty, shipTo: o.shipTo, kind: "purchase", label: c.item?.sku ?? "" });
      }
    }
    // The same part for two builds at one CM is one line; one just ordered waits until the plan counts its PO.
    const seen = new Map<string, (typeof out)[number]>();
    for (const p of out) {
      const k = pickId(p);
      const po = awaiting.get(k);
      if (po && !p.result.incoming.some((x) => x.poId === po) && !(p.result.builds ?? []).some((x) => x.poId === po)) continue;
      if (!seen.has(k)) seen.set(k, p);
    }
    return [...seen.values()];
  }, [cms, toggled, review, awaiting]);
  const groups = useMemo(() => draftGroups(picks), [picks]);

  /** Picks as a table's ticks: due rows unless unticked, others when ticked; `can`: rows with something to order. */
  const selectionFor = <T extends { id: string }>(rows: T[], due: (row: T) => boolean, canPick: (row: T) => boolean) => ({
    selected: new Set(rows.filter((row) => canPick(row) && picked(row.id, due(row))).map((row) => row.id)),
    onChange: (next: Set<string>) =>
      setToggled((prev) => {
        const out = new Set(prev);
        for (const row of rows) {
          if (!canPick(row)) continue;
          if (next.has(row.id) !== due(row)) out.add(row.id);
          else out.delete(row.id);
        }
        return out;
      }),
  });

  // Production runs --- builds for a project with a run being planned at this CM (and no make PO yet) are that run's
  // make PO, when it's the one run they can be for (runForBuilds()); otherwise the toast names the runs it could be for.
  const runs = useRows("production_run");
  const allPos = useRows("po");
  const runFor = (g: (typeof groups.groups)[number]) => runForBuilds(g, runs, allPos, (id) => dash.byItem.get(id)?.runProjectIds ?? []);
  const draftAll = async () => {
    if (busy) return;
    setBusy(true);
    const made: { id: string; number: string; label: string }[] = [];
    const unlinked: string[] = [];
    const waiting = new Map(awaiting);
    for (const g of groups.groups) {
      const { run, could } = runFor(g);
      const po = await createDraftPo(
        g.vendor,
        g.picks.map((p) => ({ item: p.result.item, link: p.result.link, qty: p.qty, result: p.result, planQty: p.qty })),
        { shipTo: g.shipTo, kind: g.kind, runId: run?.id ?? null },
      );
      if (!po) continue;
      made.push({ ...po, label: `${vendorName(g.vendor)}${g.kind === "make" ? " (build)" : ""}${run ? ` for ${run.number}` : ""}` });
      if (could.length)
        unlinked.push(`${po.number || "The draft PO"}: not linked to a run (${could.map((r) => r.number).join(" or ")} could be it): link it on the PO`);
      for (const p of g.picks) waiting.set(pickId(p), po.id);
    }
    setAwaiting(waiting);
    setBusy(false);
    setToggled(new Set());
    if (made.length) {
      setCreated(made);
      toast.ok(`Draft POs: ${made.map((m) => `${m.number} ${m.label}`).join(", ")}`);
    }
    for (const u of unlinked) toast.info(u);
    if (groups.noVendor.length) toast.error(`${groups.noVendor.map((p) => p.result.item.sku).join(", ")}: no vendor linked, so no PO`);
  };

  // Keys: / search, Esc closes the detail.
  useDetailKeys(searchRef, !!openId);

  const cmOptions = [{ value: "", label: "All CMs" }, ...dash.cms.map((c) => ({ value: c.vendorId, label: vendorName(c.vendor) }))];
  const usedProjects = new Set(dash.rows.flatMap((r) => r.projectIds));
  const projectOptions = [{ value: "", label: "All projects" }, ...projects.filter((p) => usedProjects.has(p.id)).map((p) => ({ value: p.id, label: p.name }))];
  const timelines = dash.timelines.filter((t) => !projectFilter || t.projectId === projectFilter);

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 overflow-hidden border-b border-line bg-surface px-4">
          <h1 className="text-[15px] font-semibold whitespace-nowrap">Make plan</h1>
          {tab !== "timeline" && <div ref={setSlot} className="-ml-1 flex shrink-0 items-center gap-1" />}
          <PageSearch ref={searchRef} value={query} onChange={setQuery} placeholder="Filter by SKU, name, CM…" toTable className="ml-2 w-56" />
          <div className="w-36">
            <Select value={cmFilter} options={cmOptions} onChange={(e) => setSearch({ cm: e.target.value || null })} />
          </div>
          {projectOptions.length > 2 && (
            <div className="w-40">
              <Select value={projectFilter} options={projectOptions} onChange={(e) => setSearch({ project: e.target.value || null })} />
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {created.length > 0 && (
              <span className={cx("text-[12px] whitespace-nowrap", openId && "hidden 2xl:inline")}>
                {created.map((c) => (
                  <PaneLink kind="po" id={c.id} key={c.id} to={poPath(c.id)} className="ml-2 text-accent-text hover:underline">
                    {c.number || "Draft PO"}
                  </PaneLink>
                ))}
              </span>
            )}
            <span className={cx("text-[12px] whitespace-nowrap text-muted", openId && "hidden 2xl:inline")}>
              {picks.length > 0 && `${picks.length} picked · ${groups.groups.length} PO${groups.groups.length === 1 ? "" : "s"}`}
            </span>
            {canOrder && (
              <Button variant="primary" icon={<FilePlus2 className="size-4" />} disabled={!groups.groups.length || busy} onClick={draftAll}>
                {groups.groups.length > 1 ? `Create ${groups.groups.length} draft POs` : "Create draft PO"}
              </Button>
            )}
            <AutoRunButton plan={plan} />
            <ClosuresButton />
          </div>
        </header>
        <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-line bg-surface px-3">
          {TABS.map((t) => (
            <button
              key={t.tab}
              type="button"
              title={t.hint}
              onClick={() => setSearch({ tab: t.tab === "builds" ? null : t.tab })}
              className={cx(
                "-mb-px border-b-2 px-2 py-2 text-[12px] whitespace-nowrap",
                tab === t.tab ? "border-accent text-text" : "border-transparent text-muted hover:text-text",
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <CutoverBanner />
        <StockStaleBanner />
        <div className={cx("min-h-0 flex-1 bg-surface", (tab === "timeline" || !cms.length) && "overflow-auto")}>
          {!dash.rows.length ? (
            <EmptyState icon={<Factory className="size-8" />} title="Nothing is built by a CM yet">
              An item shows here once its BOM names a make vendor (the CM that builds it): open its BOM and pick the CM. Its parts then get the builds' demand
              on the Purchasing plan.
            </EmptyState>
          ) : !cms.length ? (
            <EmptyState icon={<Factory className="size-8" />} title="Nothing matches" />
          ) : tab === "timeline" ? (
            timelines.map((t) => <MakeTimeline key={t.projectId ?? "none"} timeline={t} today={plan.today} />)
          ) : tab === "builds" ? (
            <BuildsTable cms={cms} plan={plan} openId={openId} selectionFor={selectionFor} viewsSlot={slot} />
          ) : (
            <PartsTable cms={cms} plan={plan} openId={openId} selectionFor={selectionFor} viewsSlot={slot} />
          )}
        </div>
      </div>
      {openId && <MakeDetail id={openId} />}
    </div>
  );
}

// Builds -------------------------------------------------------------------------------------------------------

type SelectionFor = <T extends { id: string }>(
  rows: T[],
  due: (row: T) => boolean,
  canPick: (row: T) => boolean,
) => { selected: Set<string>; onChange: (next: Set<string>) => void };

/** A CM's band over its rows: who it is (with a link to it), its site (or that it has none yet) and what's in it. */
function CmBand({ cm, children }: { cm: CmView; children?: ReactNode }) {
  return (
    <span className="flex items-center gap-2 text-[12px] font-normal tracking-normal text-muted normal-case">
      <span className="font-semibold text-text">{vendorName(cm.vendor)}</span>
      {cm.vendor && (
        <PaneLink kind="vendor" id={cm.vendorId} to={`/vendors?open=${cm.vendorId}`} className="text-faint hover:text-text" title="Open vendor">
          <ExternalLink className="size-3.5" />
        </PaneLink>
      )}
      {cm.site ? (
        <span>{cm.siteName}</span>
      ) : (
        <span className="text-warn" title="Add the CM's site as a ship-to from a PO (Ship to → New place), so consigned parts can ship there">
          <AlertTriangle className="mr-1 inline size-3.5" />
          No location yet: parts can't be shipped there on a PO
        </span>
      )}
      {children && <span>· {children}</span>}
    </span>
  );
}

type BuildLine = { id: string; r: MakeRow; cm: CmView };

/** What a build's parts look like, for sorting: ready, ready once shipped from HHL, then by how many are short. */
const readinessRank = (rd: Readiness | null) => (!rd ? null : rd.ready ? (rd.toShip ? 1 : 0) : 2 + rd.shortParts);
const readinessText = (rd: Readiness | null) => (!rd ? "" : rd.ready ? (rd.toShip ? `Ship ${rd.toShip} from HHL` : "Ready") : `${rd.shortParts} short`);

/** Each make item's next build, banded by CM; ticked builds go onto draft make POs. A click opens its detail. */
function BuildsTable({
  cms,
  plan,
  openId,
  selectionFor,
  viewsSlot,
}: {
  cms: CmView[];
  plan: Plan;
  openId: string | null;
  selectionFor: SelectionFor;
  viewsSlot: HTMLElement | null;
}) {
  const review = plan.settings.review_days;
  const rows = useMemo<BuildLine[]>(() => cms.flatMap((cm) => cm.rows.map((r) => ({ id: buildKey(r), r, cm }))), [cms]);
  const bands = useMemo(
    () =>
      new Map(
        cms.map((cm, i): [string, TableSection] => {
          const ready = cm.rows.filter((r) => r.readiness?.ready).length;
          const canBuild = cm.rows.filter((r) => (r.buildableNow.qty ?? 0) > 0).length;
          return [
            cm.vendorId,
            {
              key: cm.vendorId,
              order: i,
              label: (
                <CmBand cm={cm}>
                  {cm.rows.length} item{cm.rows.length === 1 ? "" : "s"} · {ready} ready to build · {canBuild} buildable today from stock there
                </CmBand>
              ),
            },
          ];
        }),
      ),
    [cms],
  );
  const columns = useMemo<Column<BuildLine>[]>(() => {
    const today = plan.today;
    return [
      { key: "photo", header: "", width: "40px", value: () => null, cell: ({ r }) => <Thumb src={r.item.photo_url} size={24} /> },
      {
        key: "sku",
        header: "SKU",
        width: "minmax(140px,1fr)",
        value: ({ r }) => r.item.sku,
        cell: ({ r }) => <span className="truncate font-mono text-[12px] font-medium">{r.item.sku || "No SKU"}</span>,
      },
      {
        // The plan's word for it and a draft BOM, in a column of their own so the SKU stays readable
        key: "plan",
        header: "Plan",
        width: "170px",
        value: ({ r }) => (r.result.bucket !== "ok" && r.result.bucket !== "upcoming" ? bucketText(r.result) : ""),
        text: ({ r }) =>
          [
            r.result.bucket !== "ok" && r.result.bucket !== "upcoming" ? bucketText(r.result) : "",
            r.result.flags.some((f) => f.code === "bom_draft") ? "Draft BOM" : "",
          ]
            .filter(Boolean)
            .join(" · "),
        cell: ({ r }) => (
          <span className="flex min-w-0 items-center gap-1.5">
            {r.result.bucket !== "ok" && r.result.bucket !== "upcoming" && <Badge tone={BUCKET_TONE[r.result.bucket]}>{bucketText(r.result)}</Badge>}
            {r.result.flags.some((f) => f.code === "bom_draft") && <Badge>Draft BOM</Badge>}
          </span>
        ),
      },
      {
        key: "item",
        header: "Item",
        width: "minmax(180px,2fr)",
        value: ({ r }) => (r.item.shopify_variant_title ? `${r.item.shopify_product_title} · ${r.item.shopify_variant_title}` : r.item.name),
        text: ({ r }) => r.item.name,
        cell: ({ r }) => (
          <span className="truncate text-[12px] text-muted" title={r.item.name}>
            {r.item.shopify_variant_title ? `${r.item.shopify_product_title} · ${r.item.shopify_variant_title}` : r.item.name}
          </span>
        ),
      },
      {
        key: "cover",
        header: "Cover",
        headerTitle: "Weeks until projected stock at HHL runs out (open make POs on their dates)",
        width: "80px",
        align: "right",
        value: ({ r }) => r.coverWeeks,
        text: ({ r }) => (r.coverWeeks === null ? "" : Math.round(r.coverWeeks * 10) / 10),
        cell: ({ r }) => (
          <span
            className={cx(
              "text-[12px] tabular-nums",
              r.result.coverDays === 0 ? "font-medium text-danger" : (r.result.coverDays ?? 1e9) <= r.result.landsOn(0) ? "text-warn" : "",
            )}
          >
            {r.coverWeeks === null
              ? "—"
              : r.result.coverDays === 0
                ? "out"
                : `${r.result.stockoutDay === null ? "~" : ""}${fmt(Math.round(r.coverWeeks * 10) / 10)} wk`}
          </span>
        ),
      },
      {
        key: "qty",
        header: "Next build",
        headerTitle: "The plan's next build: how many",
        width: "90px",
        align: "right",
        value: ({ r }) => r.next?.qty,
        cell: ({ r }) => (r.next ? <span className="font-mono text-[12px] font-medium">{fmt(r.next.qty)}</span> : <span className="text-faint">—</span>),
      },
      {
        key: "start",
        header: "Start by",
        headerTitle: "When the next build must start (the CM's build time before stock falls below its floor)",
        width: "160px",
        value: ({ r }) => r.next?.orderBy,
        text: ({ r }) => (r.next ? addPlanDays(today, r.next.orderBy) : ""),
        cell: ({ r }) =>
          r.next ? (
            <span className="truncate text-[12px]">
              <span className={cx(r.next.orderBy < 0 ? "font-medium text-danger" : r.next.orderBy <= plan.settings.review_days ? "font-medium" : "text-muted")}>
                {byText(r.next.orderBy, today)}
              </span>
              <ClosureChip delay={r.next.delay} who={r.result.vendor ? r.result.vendor.short_name || r.result.vendor.name : undefined} />
            </span>
          ) : null,
      },
      {
        key: "finished",
        header: "Finished",
        headerTitle: "When that build is due finished: at HHL, or at the CM that uses it (sub-assemblies)",
        width: "140px",
        value: ({ r }) => r.next?.arriveDay,
        text: ({ r }) => (r.next ? addPlanDays(today, r.next.arriveDay) : ""),
        cell: ({ r }) =>
          r.next ? (
            <span className="truncate text-[12px] text-muted">
              {dayText(r.next.arriveDay, today)}
              {r.next.shipTo && r.next.shipTo !== plan.mrp?.hhl && ` · at ${plan.mrp?.names.get(r.next.shipTo)}`}
            </span>
          ) : null,
      },
      {
        key: "parts",
        header: "Parts",
        headerTitle: "Its parts at the CM for this build, after earlier builds there take theirs",
        width: "minmax(200px,1.6fr)",
        value: ({ r }) => readinessRank(r.readiness),
        text: ({ r }) => readinessText(r.readiness),
        cell: ({ r }) => <ReadinessChip rd={r.readiness} today={today} site={r.site} />,
      },
      {
        key: "today",
        header: "Today",
        headerTitle: "Whole units the CM could build today from what's already there",
        width: "80px",
        align: "right",
        value: ({ r }) => r.buildableNow.qty,
        cell: ({ r }) => (
          <span
            className="font-mono text-[12px]"
            title={
              r.buildableNow.limiting
                ? `Limited by ${r.buildableNow.limiting.item.sku}: ${fmt(r.buildableNow.limiting.onHand)} there`
                : (r.buildableNow.blocked ?? undefined)
            }
          >
            {r.buildableNow.qty === null ? <span className="text-faint">—</span> : fmt(r.buildableNow.qty)}
          </span>
        ),
      },
      {
        key: "pos",
        header: "Make POs",
        width: "minmax(120px,1fr)",
        value: ({ r }) => r.openBuilds.map((x) => buildText(r, x)).join(", "),
        cell: ({ r }) => (
          <span className="min-w-0 truncate text-[12px] text-muted">
            {r.openBuilds.length ? (
              r.openBuilds.map((x, i) => (
                <span key={x.lineId}>
                  {i > 0 && ", "}
                  <PaneLink kind="po" id={x.poId} to={poPath(x.poId)} className="hover:text-accent-text hover:underline">
                    {buildText(r, x)}
                  </PaneLink>
                </span>
              ))
            ) : (
              <span className="text-faint">None</span>
            )}
          </span>
        ),
      },
      {
        key: "cm",
        header: "CM",
        width: "110px",
        value: ({ cm }) => vendorName(cm.vendor),
        cell: ({ cm }) => <span className="truncate text-muted">{vendorName(cm.vendor)}</span>,
      },
      {
        key: "why",
        header: "",
        width: "40px",
        value: () => null,
        cell: ({ r }) => (
          <CellControls>
            <WhyButton r={r.result} plan={plan} />
          </CellControls>
        ),
      },
    ];
  }, [plan]);
  const active = openId && rows.some((l) => l.r.item.id === openId) ? `b:${openId}` : null;
  return (
    <DataTable
      rows={rows}
      columns={columns}
      sectionOf={(l) => bands.get(l.cm.vendorId) ?? null}
      selection={selectionFor(
        rows,
        (l) => buildDue(l.r, review),
        (l) => !!l.r.next,
      )}
      selectable={(l) => !!l.r.next}
      activeId={active}
      onOpen={(l) => setSearch({ item: openId === l.r.item.id ? null : l.r.item.id })}
      defaultSort={{ key: "start" }}
      rowNoun={["item", "items"]}
      exportName="Make plan builds"
      viewsSlot={viewsSlot}
    />
  );
}

// Consignment ----------------------------------------------------------------------------------------------------

type PartLine = { id: string; c: ConsignLine; cm: CmView; o: { qty: number; shipTo: string | null } | null; price: number | null };

/** What HHL must buy or ship to each CM for its next builds, banded by CM; ticked parts go onto draft POs to the CM. */
function PartsTable({
  cms,
  plan,
  openId,
  selectionFor,
  viewsSlot,
}: {
  cms: CmView[];
  plan: Plan;
  openId: string | null;
  selectionFor: SelectionFor;
  viewsSlot: HTMLElement | null;
}) {
  const review = plan.settings.review_days;
  const rows = useMemo<PartLine[]>(
    () =>
      cms.flatMap((cm) =>
        cm.consignment.map((c) => {
          const o = consignOrder(c, cm);
          return { id: consignKey(cm, c), c, cm, o, price: o && c.result?.terms ? priceFor(c.result.terms, o.qty) : null };
        }),
      ),
    [cms],
  );
  const covered = cms.filter((cm) => !cm.consignment.length);
  const bands = useMemo(
    () =>
      new Map(
        cms.map((cm, i): [string, TableSection] => {
          const toBuy = cm.consignment.filter((c) => c.buy > 0).length;
          const toShip = cm.consignment.filter((c) => c.shipFromHhl > 0).length;
          return [
            cm.vendorId,
            {
              key: cm.vendorId,
              order: i,
              label: (
                <CmBand cm={cm}>
                  {toBuy} to buy ·{" "}
                  {toShip > 0 ? (
                    <ShipFromHhl
                      site={cm.site}
                      lines={cm.consignment.filter((c) => c.shipFromHhl > 0).map((c) => ({ itemId: c.itemId, qty: c.shipFromHhl }))}
                      label={`${toShip} to ship from HHL`}
                      className="text-accent-text"
                    />
                  ) : (
                    "0 to ship from HHL"
                  )}{" "}
                  · for {cm.builds.length} build{cm.builds.length === 1 ? "" : "s"}
                </CmBand>
              ),
            },
          ];
        }),
      ),
    [cms],
  );
  const columns = useMemo<Column<PartLine>[]>(() => {
    const today = plan.today;
    const num = (n: number, zero?: boolean) => (n || !zero ? fmt(n) : <span className="text-faint">0</span>);
    return [
      { key: "photo", header: "", width: "40px", value: () => null, cell: ({ c }) => <Thumb src={c.result?.item.photo_url} size={24} /> },
      {
        key: "sku",
        header: "SKU",
        width: "minmax(150px,1fr)",
        value: ({ c }) => c.item?.sku ?? "",
        cell: ({ c }) => <span className="truncate font-mono text-[12px] font-medium">{c.item?.sku || "No SKU"}</span>,
      },
      {
        key: "part",
        header: "Part",
        width: "minmax(170px,2fr)",
        value: ({ c }) => c.item?.name ?? "",
        cell: ({ c }) => (
          <span className="truncate text-[12px] text-muted" title={c.item?.name}>
            {c.item?.name}
          </span>
        ),
      },
      {
        key: "for",
        header: "For",
        headerTitle: "The builds that take it",
        width: "minmax(120px,1fr)",
        value: ({ c }) => c.builds.join(", "),
        cell: ({ c }) => (
          <span className="truncate text-[12px] text-muted" title={`For ${c.builds.join(", ")}`}>
            {c.builds.join(", ")}
          </span>
        ),
      },
      {
        key: "need",
        header: "Need",
        headerTitle: "What the CM's next builds take of it (open make POs first)",
        width: "80px",
        align: "right",
        value: ({ c }) => Math.round(c.need),
        cell: ({ c }) => <span className="font-mono text-[12px]">{num(Math.round(c.need))}</span>,
      },
      {
        key: "atCm",
        header: "At CM",
        width: "80px",
        align: "right",
        value: ({ c }) => Math.round(c.atCm),
        cell: ({ c }) => <span className="font-mono text-[12px]">{num(Math.round(c.atCm))}</span>,
      },
      {
        key: "toCm",
        header: "On PO to CM",
        headerTitle: "On POs shipping to the CM",
        width: "100px",
        align: "right",
        value: ({ c }) => Math.round(c.toCm),
        cell: ({ c }) => <span className="font-mono text-[12px]">{num(Math.round(c.toCm), true)}</span>,
      },
      {
        key: "fromHhl",
        header: "From HHL",
        headerTitle: "At HHL now: ship it over (or forward what lands at HHL on a PO)",
        width: "90px",
        align: "right",
        value: ({ c }) => Math.round(c.shipFromHhl + c.forward),
        cell: ({ c }) => (
          <span className="font-mono text-[12px]" title={c.forward ? `${fmt(Math.round(c.forward))} more lands at HHL on a PO: forward it` : undefined}>
            {num(Math.round(c.shipFromHhl + c.forward), true)}
          </span>
        ),
      },
      {
        key: "buy",
        header: "Buy",
        headerTitle: "Short: HHL must buy this much more",
        width: "80px",
        align: "right",
        value: ({ c }) => Math.ceil(c.buy),
        cell: ({ c }) => <span className={cx("font-mono text-[12px]", c.buy > 0 && "font-medium")}>{num(Math.ceil(c.buy), true)}</span>,
      },
      {
        key: "needed",
        header: "Needed by",
        headerTitle: "When the first build that's short of it starts",
        width: "100px",
        value: ({ c }) => c.byDay,
        text: ({ c }) => addPlanDays(today, c.byDay),
        cell: ({ c }) => <span className="truncate text-[12px] text-muted">{dayText(c.byDay, today)}</span>,
      },
      {
        key: "orderBy",
        header: "Order by",
        headerTitle: "Needed by − the part's lead time",
        width: "150px",
        value: ({ c }) => c.orderBy ?? c.byDay,
        text: ({ c }) => (c.buy > 0 && c.orderBy !== null ? addPlanDays(today, c.orderBy) : ""),
        cell: ({ c }) => (
          <span
            className={cx(
              "truncate text-[12px]",
              c.orderBy !== null && c.orderBy < 0 ? "font-medium text-danger" : (c.orderBy ?? 1e9) <= plan.settings.review_days ? "font-medium" : "text-muted",
            )}
          >
            {c.buy > 0 && c.orderBy !== null ? byText(c.orderBy, today) : "—"}
            {c.buy > 0 && <ClosureChip delay={c.delay} who={c.result?.vendor ? vendorName(c.result.vendor) : undefined} />}
          </span>
        ),
      },
      {
        key: "order",
        header: "Planned order",
        headerTitle: "The plan's order for it to this CM (else the shortfall on the vendor's terms)",
        width: "minmax(120px,1fr)",
        value: ({ o }) => o?.qty,
        cell: ({ c, o }) =>
          o && c.result ? (
            <span className="flex min-w-0 items-center gap-1 text-[12px]">
              <span className="font-mono font-medium">{fmt(o.qty)}</span>
              {c.order?.priceHint && (
                <span
                  className="truncate text-[11px] text-accent-text"
                  title={`+${fmt(c.order.priceHint.extra)} reaches the ${fmt(c.order.priceHint.qty)} price: saves ${money(c.order.priceHint.saves, c.result.currency)}`}
                >
                  +{fmt(c.order.priceHint.extra)} → {money(c.order.priceHint.unitPrice, c.result.currency)}
                </span>
              )}
            </span>
          ) : c.buy > 0 ? (
            <span className="text-faint">—</span>
          ) : (
            <span className="text-[12px] text-muted">Ship from HHL</span>
          ),
      },
      {
        key: "vendor",
        header: "Vendor",
        width: "120px",
        value: ({ c }) => (c.result?.vendor ? vendorName(c.result.vendor) : ""),
        cell: ({ c, o }) =>
          o && c.result ? (
            c.result.vendor ? (
              <span className="truncate text-[12px] text-muted">{vendorName(c.result.vendor)}</span>
            ) : (
              <span className="text-[12px] text-danger">no vendor</span>
            )
          ) : null,
      },
      {
        key: "cost",
        header: "Cost",
        headerTitle: "The planned order at the vendor's price",
        width: "100px",
        align: "right",
        value: ({ o, price }) => (o && price != null ? mulRound(o.qty, price, 2) : null),
        cell: ({ c, o, price }) =>
          o && c.result && price != null ? <span className="text-[12px] text-muted">{money(mulRound(o.qty, price, 2), c.result.currency)}</span> : null,
      },
      {
        key: "cm",
        header: "CM",
        width: "110px",
        value: ({ cm }) => vendorName(cm.vendor),
        cell: ({ cm }) => <span className="truncate text-muted">{vendorName(cm.vendor)}</span>,
      },
    ];
  }, [plan]);
  const active = openId ? (rows.find((l) => l.c.itemId === openId)?.id ?? null) : null;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {covered.map((cm) => (
        <div key={cm.vendorId} className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-2 text-[12px] text-ok">
          <CheckCircle2 className="size-4" /> Everything the next builds need is at {vendorName(cm.vendor)} or on POs shipping there.
        </div>
      ))}
      {rows.length > 0 && (
        <div className="min-h-0 flex-1">
          <DataTable
            rows={rows}
            columns={columns}
            sectionOf={(l) => bands.get(l.cm.vendorId) ?? null}
            selection={selectionFor(
              rows,
              (l) => consignDue(l.c, review),
              (l) => !!l.o,
            )}
            selectable={(l) => !!l.o}
            activeId={active}
            onOpen={(l) => setSearch({ item: l.c.itemId })}
            defaultSort={{ key: "orderBy" }}
            rowNoun={["part", "parts"]}
            exportName="Make plan parts to send"
            viewsSlot={viewsSlot}
          />
        </div>
      )}
    </div>
  );
}

const bucketText = (r: ItemPlanResult) =>
  ({
    order_now: "Build now",
    at_risk: "At risk",
    this_month: "This month",
    upcoming: "Upcoming",
    overstock: "Overstock",
    ok: "OK",
    snoozed: "Snoozed",
    not_planned: "Not planned",
  })[r.bucket];

function ReadinessChip({ rd, today, site }: { rd: Readiness | null; today: string; site: string | null }) {
  if (!rd) return <span className="text-[12px] text-faint">No build planned</span>;
  if (rd.ready && rd.toShip)
    return (
      <span
        className="flex items-center gap-1.5 text-[12px] text-muted"
        title="Covered, but only by stock at HHL (or POs to HHL): ship it over before the build starts. Click to ship it."
      >
        <Badge tone="accent">
          <ShipFromHhl
            site={site}
            lines={rd.lines.filter((l) => l.atHhl > 0).map((l) => ({ itemId: l.itemId, qty: l.atHhl }))}
            label={`Ship ${rd.toShip} from HHL`}
          />
        </Badge>
        {rd.readyDay ? `by ${dayText(rd.readyDay, today)}` : ""}
      </span>
    );
  if (rd.ready)
    return (
      <span className="flex items-center gap-1 text-[12px] text-ok">
        <CheckCircle2 className="size-3.5" /> Parts ready{rd.readyDay ? ` by ${dayText(rd.readyDay, today)}` : ""}
      </span>
    );
  const g = rd.gating;
  const late = rd.readyDay === null || rd.readyDay > Math.max(0, rd.build.start);
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <Badge tone={late ? "danger" : "warn"}>{rd.shortParts ? `${rd.shortParts} short` : "Waiting"}</Badge>
      {g && (
        <span className="truncate text-[12px] text-muted" title={g.item?.name}>
          <span className="font-mono text-text">{g.item?.sku}</span> {g.readyDay === null ? "not ordered" : `by ${dayText(g.readyDay, today)}`}
        </span>
      )}
    </span>
  );
}

// Detail panel ---------------------------------------------------------------------------------------------

function MakeDetail({ id }: { id: string }) {
  const item = useRow("item", id);
  const { plan, dash } = useMakeDashboard();
  const row = dash.byItem.get(id);
  const r = plan.byItem.get(id);
  if (!item) return null;
  return (
    <PlanDetail
      item={item}
      about={r?.vendor ? vendorName(r.vendor) : null}
      links={
        row && (
          <Link to={`/boms?open=${row.spec.bom.id}`} className="text-accent-text hover:underline">
            BOM Rev {row.spec.bom.revision}
          </Link>
        )
      }
      className="w-[540px]"
    >
      {row?.readiness && <ReadinessTable rd={row.readiness} plan={plan} site={row.site} />}
      {r && r.orders.length > 0 && <PlannedOrders r={r} plan={plan} />}
      {r ? <ItemPlanSection item={item} inPlan /> : <div className="p-2 text-muted">Not planned: it isn't in a make item's BOM.</div>}
      {item.kind === "sell" && <ForecastSection item={item} />}
    </PlanDetail>
  );
}

type ReadyLine = Readiness["lines"][number] & { id: string; position: number };

/** The build's parts HHL supplies: where each stands for this build (the usual table; a row opens the part). */
function ReadinessTable({ rd, plan, site }: { rd: Readiness; plan: Plan; site: string | null }) {
  const t = plan.today;
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- by the title
  const n = (v: number) => (v > 0 ? fmt(Math.round(v)) : <span className="text-faint">0</span>);
  const rows: ReadyLine[] = rd.lines.map((l, i) => ({ ...l, id: l.itemId, position: i + 1 }));
  // The part that holds the build up is in bold.
  const cell = (l: ReadyLine, node: ReactNode, className?: string, title?: string) => (
    <span className={cx("truncate font-mono", rd.gating?.itemId === l.itemId && "font-medium", className)} title={title}>
      {node}
    </span>
  );
  const columns: Column<ReadyLine>[] = [
    {
      key: "sku",
      header: "Part",
      width: "minmax(90px,1fr)",
      value: (l) => l.item?.sku ?? "",
      cell: (l) => cell(l, l.item?.sku, "", `${l.item?.name} · ${fmt(l.perUnit)} per unit`),
    },
    { key: "name", header: "Name", optional: true, width: "minmax(120px,1fr)", value: (l) => l.item?.name ?? "" },
    { key: "need", header: "Need", width: "64px", align: "right", value: (l) => Math.round(l.need), cell: (l) => cell(l, fmt(Math.round(l.need))) },
    { key: "atCm", header: "At CM", width: "64px", align: "right", value: (l) => Math.round(l.atCm), cell: (l) => cell(l, n(l.atCm)) },
    {
      key: "toCm",
      header: "To CM",
      headerTitle: "On POs shipping to the CM",
      width: "64px",
      align: "right",
      value: (l) => Math.round(l.toCm),
      cell: (l) => cell(l, n(l.toCm), "", l.toCmEta !== null ? `Lands ${dayText(l.toCmEta, t)}` : undefined),
    },
    {
      key: "hhl",
      header: "HHL",
      headerTitle: "At HHL, or on POs to HHL: ship it on",
      width: "64px",
      align: "right",
      value: (l) => Math.round(l.atHhl + l.onOrder),
      cell: (l) =>
        cell(l, n(l.atHhl + l.onOrder), "", l.onOrderEta !== null ? `${fmt(Math.round(l.onOrder))} lands at HHL ${dayText(l.onOrderEta, t)}` : undefined),
    },
    {
      key: "short",
      header: "Short",
      width: "64px",
      align: "right",
      value: (l) => Math.ceil(l.short),
      cell: (l) => cell(l, n(Math.ceil(l.short)), l.short > 0 ? "text-danger" : ""),
    },
    {
      key: "ready",
      header: "Ready",
      width: "92px",
      value: (l) => l.readyDay ?? 1e9,
      text: (l) => (l.readyDay === null ? "Not ordered" : dayText(l.readyDay, t)),
      cell: (l) => (
        <span
          className={cx(
            "truncate",
            rd.gating?.itemId === l.itemId && "font-medium",
            l.readyDay === null ? "text-danger" : (l.readyDay ?? 0) > Math.max(0, rd.build.start) ? "text-warn" : "text-muted",
          )}
        >
          {l.readyDay === null ? "Not ordered" : dayText(l.readyDay, t)}
        </span>
      ),
    },
    // The build's own order (what it's sorted by until someone sorts it another way).
    { key: "position", header: "#", optional: true, width: "48px", align: "right", value: (l) => l.position },
  ];
  return (
    <section className="px-2 pb-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pb-1">
        <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">
          Parts for {rd.build.poNumber ? rd.build.poNumber : "the next build"} · {fmt(rd.build.qty)}
        </h3>
        <span className="text-[11px] text-faint">starts {dayText(rd.build.start, t)}</span>
        <span className="ml-auto">
          <ReadinessChip rd={rd} today={t} site={site} />
        </span>
        <div ref={setSlot} className="flex items-center gap-1" />
      </div>
      {rd.lines.length > 0 ? (
        <DataTable
          fit
          rows={rows}
          columns={columns}
          defaultSort={{ key: "position" }}
          onOpen={(l) => setSearch({ item: l.itemId })}
          rowNoun={["part", "parts"]}
          exportName="Parts for the build"
          viewsSlot={slot}
          pageFilters={false}
        />
      ) : (
        <div className="text-muted">HHL supplies nothing for it: the CM buys every part.</div>
      )}
    </section>
  );
}

type OrderLine = MrpOrder & { id: string };

function PlannedOrders({ r, plan }: { r: ItemPlanResult; plan: Plan }) {
  const t = plan.today;
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- by the title
  const sku = (id: string) => plan.byItem.get(id)?.item.sku ?? "";
  const what = (o: MrpOrder) =>
    o.pegs
      .slice(0, 3)
      .map((p) =>
        p.kind === "build" && p.demand
          ? `${fmt(Math.round(p.qty))} for ${p.demand.poNumber ?? `the ${sku(p.demand.parentId)} build`} ${dayText(p.demand.buildDay, t)}`
          : `${fmt(Math.round(p.qty))} ${p.kind === "b2b" ? "B2B" : p.kind === "reserved" ? "reserved (pre-sale or held)" : "sales"} ${dayText(p.from, t)}–${dayText(p.to, t)}`,
      )
      .join("; ") || "Safety stock / MOQ";
  const rows: OrderLine[] = r.orders.slice(0, 12).map((o) => ({ ...o, id: o.key }));
  const columns: Column<OrderLine>[] = [
    {
      key: "orderBy",
      header: r.role === "make" ? "Start by" : "Order by",
      width: "140px",
      value: (o) => o.orderBy,
      text: (o) => addPlanDays(t, o.orderBy),
      cell: (o) => (
        <span className={cx("truncate", o.orderBy < 0 ? "text-danger" : "text-muted")}>
          {byText(o.orderBy, t)}
          <ClosureChip delay={o.delay} />
        </span>
      ),
    },
    { key: "qty", header: "Qty", width: "72px", align: "right", value: (o) => o.qty, cell: (o) => <span className="font-mono">{fmt(o.qty)}</span> },
    {
      key: "arrive",
      header: r.role === "make" ? "At HHL" : "Lands",
      width: "92px",
      value: (o) => o.arriveDay,
      text: (o) => addPlanDays(t, o.arriveDay),
      cell: (o) => <span className="truncate text-muted">{dayText(o.arriveDay, t)}</span>,
    },
    {
      key: "for",
      header: "For",
      width: "minmax(160px,1fr)",
      value: (o) => what(o),
      cell: (o) => (
        <span className="truncate text-muted" title={what(o)}>
          {what(o)}
        </span>
      ),
    },
  ];
  return (
    <section className="mt-1 border-t border-line px-2 pt-2 pb-2">
      <div className="flex items-center gap-2 pb-1">
        <h3 className="text-[11px] font-medium tracking-wide text-faint uppercase">Planned {r.role === "make" ? "builds" : "orders"} · next 12 months</h3>
        <div ref={setSlot} className="ml-auto flex items-center gap-1" />
      </div>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        rowNoun={r.role === "make" ? ["build", "builds"] : ["order", "orders"]}
        exportName="Planned orders"
        viewsSlot={slot}
        pageFilters={false}
      />
      {r.orders[0]?.chain.length ? (
        <div className="mt-1.5 text-[11px] text-muted">
          Cumulative lead time {r.orders[0].cumLead} days: {ownLead(r.orders[0])} for it, then{" "}
          {r.orders[0].chain.map((c) => `${sku(c.itemId)} ${c.lead}d`).join(" → ")}.
        </div>
      ) : null}
    </section>
  );
}
